import {
  ClientData,
  ConfigurationItem,
  ExchangeAccountType,
  FixMeLater,
  GroupData,
  SortingOrder,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import { get, unionBy } from "lodash";
import { v4 as uuid } from "uuid";
import { MAX_ACCOUNT_NAME_LENGTH } from "@shared/common/constants";
import { DEMO_ACCOUNT_INITIAL_FUNDS_RANGE } from "@shared/features/portfolio/constants";

export const getItemValue = (item: FixMeLater, idToItem?: FixMeLater) =>
  getItemKey(item, "value", idToItem);

export const testCondition = (
  condition: FixMeLater,
  idToItem: FixMeLater,
  idToValue?: FixMeLater
) => {
  const { constraints } = condition;
  if (!constraints) return true;
  if (constraints.length) {
    return constraints.every(({ field_id: fieldId, values }: any) => {
      const constraintItem = idToItem && idToItem[fieldId];
      let constraintValue: any = idToValue?.[fieldId];
      if (constraintItem) {
        constraintValue = getItemValue(constraintItem);
      }
      if (
        constraintValue !== undefined &&
        (values.indexOf(constraintValue) >= 0 ||
          values.indexOf(parseInt(constraintValue)) >= 0)
      ) {
        return true;
      }
      return false;
    });
  }
};

export const getFilledCondition = (
  item: FixMeLater,
  key: string,
  idToItem: FixMeLater,
  idToValue: FixMeLater
) => {
  const valueConditionsArray = item?.[key] || [];
  if (!valueConditionsArray) return;

  if (Array.isArray(valueConditionsArray)) {
    for (let condition of valueConditionsArray) {
      if (testCondition(condition, idToItem, idToValue)) {
        return condition;
      }
    }
  } else {
    if (testCondition(valueConditionsArray, idToItem, idToValue)) {
      return valueConditionsArray;
    }
  }
};

export const itemShouldDisplay = (item: any, idToItem: any, idToValue?: any) =>
  getItemKey(item, "should_display", idToItem, idToValue) !== false;

export const getItemKey = (
  item: FixMeLater,
  key: FixMeLater,
  idToItem?: FixMeLater,
  idToValue?: FixMeLater
) => {
  const filledCondition = getFilledCondition(item, key, idToItem, idToValue);
  return filledCondition
    ? "outcome" in filledCondition
      ? filledCondition.outcome
      : filledCondition
    : null;
};

export const getName = (item: FixMeLater) => getItemKey(item, "name");
export const getItemValidations = (item: FixMeLater) =>
  getItemKey(item, "validations");
export const getChildren = (item: any) => get(item, ["data"], []);
export const getCreateItem = (item: any) => getItemKey(item, "create");
export const getIsSectionHeader = (item: any) =>
  getItemKey(item, "is_section_header");
export const getItemOptions = (item: any, idToItem?: any) =>
  getItemKey(item, "options", idToItem);
export const getItemByName = (name: string, item: any): any => {
  if (!item) return;
  const itemName = getName(item);
  if (itemName && itemName.toLowerCase() === name.toLowerCase()) {
    return item;
  }
  const children = getChildren(item);
  for (let child of children) {
    const item = getItemByName(name, child);
    if (item !== undefined) return item;
  }
};

export const updateClientsAndGroupsFromLiveSearch = (
  groupsData: GroupData[] | undefined,
  dispatch: any
) => {
  dispatch(
    sharedLib.sharedApi.settings.util.updateQueryData(
      "getGroupsData",
      undefined,
      (subGroupsData) => {
        if (subGroupsData) {
          return unionBy(groupsData, subGroupsData, "id");
        }
      }
    )
  );
};

export const testDependentItem = (
  item: ConfigurationItem,
  parentItemId?: string
) => {
  for (let option of item.options || []) {
    if (
      (option.constraints || []).some(
        (constraint: any) => constraint.field_id === parentItemId
      )
    ) {
      return true;
    }
  }
};

export const dependentItemExists = (
  parentItemId: string,
  allItems: ConfigurationItem[]
) => {
  for (let item of allItems) {
    if (testDependentItem(item, parentItemId)) {
      return true;
    }
  }
  return false;
};

export const addUuidToConfigurationItems = (item?: ConfigurationItem) => {
  if (!item) return;
  (item as any).uuid = uuid();

  const children = getChildren(item);

  children.forEach((childItem: ConfigurationItem) =>
    addUuidToConfigurationItems(childItem)
  );
};

export const getAllConfigurationItems = (
  clonedConfiguration?: ConfigurationItem
) => {
  return getAllItems(clonedConfiguration).reduce((acc, item) => {
    const itemId = getItemId(item);
    if (itemId) {
      // @ts-ignore
      acc[itemId] = item;
    }
    return acc;
  }, {});
};

export const convertValues = (
  item: ConfigurationItem | undefined,
  idToItem: Record<string, ConfigurationItem>,
  allItems: ConfigurationItem[],
  parentShouldDisplay = true
) => {
  if (!item) return;
  const shouldDisplay = itemShouldDisplay(item, idToItem);
  if ("value" in item) {
    let value = getItemValue(item, idToItem);
    const itemId = getItemId(item);
    const name = item?.name?.[0]?.outcome;

    if (!parentShouldDisplay) {
      value = null;
    } else if (!shouldDisplay) {
      if (
        name &&
        typeof name === "string" &&
        name.toLowerCase() === "multiplier"
      ) {
        value = 0;
      } else if (!itemId || !dependentItemExists(itemId, allItems)) {
        value = null;
      }
    }

    item["value"] = value;
  }
  if ("override" in item) {
    (item as any)["override"] = getItemOverride(item);
  }
  const children = getChildren(item);
  children.forEach((childItem: ConfigurationItem) =>
    convertValues(childItem, idToItem, allItems, shouldDisplay)
  );
};

const getAllItemsRecursive = (
  item: ConfigurationItem | undefined,
  allItems: ConfigurationItem[]
) => {
  if (!item) return;
  allItems.push(item);

  const children = getChildren(item);
  children.forEach((childItem: ConfigurationItem) => {
    getAllItemsRecursive(childItem, allItems);
  });
};

export const getAllItems = (configuration: ConfigurationItem | undefined) => {
  const allItems: ConfigurationItem[] = [];
  getAllItemsRecursive(configuration, allItems);
  return allItems;
};

export const getItemId = (item: ConfigurationItem) => getItemKey(item, "id");
export const getItemOverride = (item: ConfigurationItem) =>
  getItemKey(item, "override");

export const getOptions = (data: any) => {
  return data?.options?.[0]?.outcome;
};

export const sortByActivity = <T>(
  a: T,
  b: T,
  getNumConnections: (group: T) => number,
  sortOrder = SortingOrder.DESCEND,
  isBinary = true
) => {
  let sort;
  if (isBinary) {
    sort = +(getNumConnections(b) > 0) - +(getNumConnections(a) > 0);
  } else {
    sort = getNumConnections(b) - getNumConnections(a);
  }
  return -sortOrder * sort;
};

export const sortGroupsExpiration = (a: GroupData, b: GroupData) => {
  const hasExpirationSort =
    +!!b.expiration_timestamp - +!!a.expiration_timestamp;
  if (hasExpirationSort) return hasExpirationSort;
  else if (a.expiration_timestamp && b.expiration_timestamp) {
    const sortOrder = a.allow_follow
      ? SortingOrder.ASCEND
      : SortingOrder.DESCEND;
    return sortOrder * (a.expiration_timestamp - b.expiration_timestamp);
  }
};

export const getUuidToItems = (configurationWithUuid?: ConfigurationItem) =>
  getAllItems(configurationWithUuid).reduce((acc, item) => {
    // @ts-ignore
    acc[item.uuid] = item;
    return acc;
  }, {});

export const setItemValue = (item: any, value: any, idToItem?: any) => {
  const filledCondition = getFilledCondition(
    item,
    "value",
    idToItem,
    undefined
  );
  if (filledCondition) {
    filledCondition.outcome = value;
  }
};

export const getItemValueType = (item: FixMeLater, idToItem?: FixMeLater) => {
  const itemValue = getItemValue(item, idToItem) || {};
  return itemValue.type;
};

export const checkStrategyItemSelected = (
  strategyItem: FixMeLater,
  itemValue: FixMeLater
) => {
  const childType = getItemValueType(strategyItem);
  return itemValue && childType
    ? itemValue.toString() === childType.toString()
    : strategyItem.chosen;
};

// @ts-ignore
export const findChildElementWithMatchingValueType = (
  item: FixMeLater,
  itemValueType: FixMeLater
) => {
  const children = getChildren(item);
  for (let childItem of children) {
    if (checkStrategyItemSelected(childItem, itemValueType)) {
      return childItem;
    }
  }
  for (let childItem of children) {
    // @ts-ignore
    const value = findChildElementWithMatchingValueType(
      childItem,
      itemValueType
    );
    if (value) {
      return value;
    }
  }
};

export const getItemSuffix = (item: FixMeLater, idToItem?: FixMeLater) =>
  getItemKey(item, "suffix", idToItem);
export const getItemPrefix = (item: FixMeLater, idToItem?: FixMeLater) =>
  getItemKey(item, "prefix", idToItem);

export const getAccountNameError = () =>
  `Account name should have ${MAX_ACCOUNT_NAME_LENGTH} characters or less.`;

export const validateDemoAccountInitialFunds = (value: number) => {
  const range = DEMO_ACCOUNT_INITIAL_FUNDS_RANGE;
  if (value < range[0] || value > range[1]) {
    return `Please choose a value between ${range[0]} and ${range[1]}`;
  }
};

export const getIsMicaAccount = (account?: ClientData) => {
  return (
    account?.exchange_account_type === ExchangeAccountType.MICA_RESTRICTED ||
    account?.exchange_account_type ===
      ExchangeAccountType.MICA_PARTIALLY_RESTRICTED
  );
};

export const getInactiveGroupAutoTradingWarning = (isWhitelabel?: boolean) => {
  return `This group is no longer connected to ${
    isWhitelabel ? "the platform" : "Cornix"
  }.`;
};

export const getSignalsBotsMigrationBannerTitle = (isWhitelabel?: boolean) => {
  return `Signals bots are the way to follow signals${
    isWhitelabel ? "" : " using Cornix"
  }.`;
};
